<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isTopFairDialog, pushFairDialog, removeFairDialog } from './fairDialogStack'
import { useFairOverlay } from '@/ui/primitives/FairOverlay'
import Dialog from 'primevue/dialog'
import FairDialogPanel from './FairDialogPanel.vue'
import { FAIR_DIALOG_DEFAULT_WIDTH } from '@/ui/tokens/dialog.data'
import type { FairIconName } from '@/ui/tokens/icons.data'

interface FairDialogProps {
  visible?: boolean
  header?: string
  closable?: boolean
  showHeader?: boolean
  showFooter?: boolean
  showCancel?: boolean
  cancelLabel?: string
  confirmLabel?: string
  cancelLeadingIcon?: FairIconName
  cancelTrailingIcon?: FairIconName
  confirmLeadingIcon?: FairIconName
  confirmTrailingIcon?: FairIconName
  modal?: boolean
  dismissableMask?: boolean
  closeOnEscape?: boolean
  width?: string
  contentText?: string
  /** The main button is the disabled chip until something can be submitted. */
  confirmDisabled?: boolean
  confirmLoading?: boolean
  /**
   * DS Modal Size: `default` (centred panel), `mobile` (bottom sheet), or
   * `auto` — mobile below the 1024 breakpoint.
   */
  size?: 'default' | 'mobile' | 'auto'
  /** Mobile `Left button` — a back arrow before the title. */
  showBack?: boolean
  closeLabel?: string
  backLabel?: string
  /** Fixed desktop height (e.g. `540px`) so inner screens of any length scroll instead of resizing. */
  height?: string | null
}

const props = withDefaults(defineProps<FairDialogProps>(), {
  visible: false,
  header: 'Header',
  closable: true,
  showHeader: true,
  showFooter: true,
  showCancel: true,
  cancelLabel: 'Для себя',
  confirmLabel: 'Для себя',
  cancelLeadingIcon: undefined,
  cancelTrailingIcon: undefined,
  confirmLeadingIcon: undefined,
  confirmTrailingIcon: undefined,
  modal: true,
  dismissableMask: true,
  closeOnEscape: true,
  width: FAIR_DIALOG_DEFAULT_WIDTH,
  contentText: undefined,
  confirmDisabled: false,
  confirmLoading: false,
  size: 'default',
  showBack: false,
  closeLabel: 'Close',
  backLabel: 'Back',
  height: null,
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
  back: []
  cancel: [event: MouseEvent]
  confirm: [event: MouseEvent]
}>()

const compactQuery = ref<MediaQueryList | null>(null)
const isCompactViewport = ref(false)

function syncViewport() {
  isCompactViewport.value = compactQuery.value?.matches ?? false
}

onMounted(() => {
  compactQuery.value = window.matchMedia('(max-width: 1023px)')
  syncViewport()
  compactQuery.value.addEventListener('change', syncViewport)
})

onBeforeUnmount(() => {
  compactQuery.value?.removeEventListener('change', syncViewport)
})

const isMobile = computed(
  () => props.size === 'mobile' || (props.size === 'auto' && isCompactViewport.value),
)

const dialogStyle = computed(() =>
  isMobile.value
    ? { width: '100vw' }
    : { width: props.width, ...(props.height ? { height: props.height } : {}) },
)

/** The panel element — callers measure it (Style Space item dialog locks its height). */
const panelRef = ref<InstanceType<typeof FairDialogPanel> | null>(null)
const panel = computed<HTMLElement | null>(() => (panelRef.value?.$el as HTMLElement | undefined) ?? null)

defineExpose({ panel })

const stackId = Symbol('fair-dialog')

// Non-modal dialogs leave the page usable — they neither lock the scroll nor count as an overlay.
useFairOverlay(() => props.visible && props.modal)

function handleEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !props.closeOnEscape || !isTopFairDialog(stackId)) return
  event.stopPropagation()
  handleClose()
}

watch(
  () => props.visible,
  (visible) => {
    if (typeof window === 'undefined') return
    if (visible) {
      pushFairDialog(stackId)
      window.addEventListener('keydown', handleEscape, true)
    } else {
      removeFairDialog(stackId)
      window.removeEventListener('keydown', handleEscape, true)
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  removeFairDialog(stackId)
  if (typeof window !== 'undefined') window.removeEventListener('keydown', handleEscape, true)
})

function setVisible(value: boolean) {
  emit('update:visible', value)
}

function handleClose() {
  setVisible(false)
  emit('close')
}

function handleCancel(event: MouseEvent) {
  emit('cancel', event)
}

function handleConfirm(event: MouseEvent) {
  emit('confirm', event)
}

function handleHide() {
  setVisible(false)
  emit('close')
}
</script>

<template>
  <Dialog
    :visible="visible"
    class="fair-dialog"
    :modal="modal"
    :block-scroll="false"
    :dismissable-mask="dismissableMask"
    :closable="false"
    :close-on-escape="false"
    :show-header="false"
    :position="isMobile ? 'bottom' : 'center'"
    :style="dialogStyle"
    :pt="{
      root: { class: ['fair-dialog', { 'fair-dialog--mobile': isMobile, 'fair-dialog--fixed-height': Boolean(height) && !isMobile }] },
      mask: { class: 'fair-dialog-mask' },
    }"
    @update:visible="setVisible"
    @hide="handleHide"
  >
    <template #container="{ closeCallback }">
      <FairDialogPanel
        ref="panelRef"
        :header="header"
        :closable="closable"
        :show-header="showHeader"
        :show-footer="showFooter"
        :show-cancel="showCancel"
        :cancel-label="cancelLabel"
        :confirm-label="confirmLabel"
        :cancel-leading-icon="cancelLeadingIcon"
        :cancel-trailing-icon="cancelTrailingIcon"
        :confirm-leading-icon="confirmLeadingIcon"
        :confirm-trailing-icon="confirmTrailingIcon"
        :content-text="contentText"
        :confirm-disabled="confirmDisabled"
        :confirm-loading="confirmLoading"
        :mobile="isMobile"
        :show-back="showBack"
        :close-label="closeLabel"
        :back-label="backLabel"
        @back="emit('back')"
        @close="closeCallback(); handleClose()"
        @cancel="handleCancel"
        @confirm="handleConfirm"
      >
        <template
          v-if="$slots['header-start']"
          #header-start
        >
          <slot name="header-start" />
        </template>
        <template
          v-if="$slots['header-end']"
          #header-end
        >
          <slot name="header-end" />
        </template>
        <template
          v-if="$slots.header"
          #header
        >
          <slot name="header" />
        </template>
        <template
          v-if="$slots.default"
          #default
        >
          <slot />
        </template>
        <template
          v-if="$slots.notice"
          #notice
        >
          <slot name="notice" />
        </template>
        <template
          v-if="$slots.footer"
          #footer
        >
          <slot name="footer" />
        </template>
      </FairDialogPanel>
    </template>
  </Dialog>
</template>
